import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import { doc, onSnapshot, deleteDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { FaUndo, FaSpinner, FaTimes, FaCheck } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const RefundButton = ({ order }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRefunded, setIsRefunded] = useState(order.refunded || false);
  const { currentUser, setWalletBalance } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  // Listen for order changes to mark it refunded if it no longer exists or has the refunded flag.
  useEffect(() => {
    if (!order.id) return;
    const unsubscribe = onSnapshot(doc(db, "finalOrder", order.id), (docSnapshot) => {
      if (!docSnapshot.exists() || (docSnapshot.data() && docSnapshot.data().refunded)) {
        setIsRefunded(true);
      }
    });
    return () => unsubscribe();
  }, [order.id]);

  // Calculate refund expiration timer (3 minutes).
  useEffect(() => {
    if (!order.date?.seconds || order.isUrgent) return;
    const orderTime = new Date(order.date.seconds * 1000);
    const elapsed = (new Date() - orderTime) / 60000;
    setTimeLeft(Math.max(3 - Math.floor(elapsed), 0));
    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev - 1, 0));
    }, 60000);
    return () => clearInterval(interval);
  }, [order]);

  const handleRefund = async () => {
    if (isRefunded) {
      toast.error("Refund already processed");
      return;
    }
    setIsProcessing(true);
    try {
      if (!currentUser?.uid || !order.id) {
        toast.error("Invalid request parameters");
        return;
      }

      // Refund the full order amount (No tax or fee deductions)
      const refundAmount = order.totalAmount;

      const response = await fetch("https://processrefund-qwfnnmalla-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderId: order.id,
          userId: order.userId,
          amount: refundAmount,
        }),
      });
      if (!response.ok) throw new Error("Refund processing failed");

      await deleteDoc(doc(db, "finalOrder", order.id));

      setWalletBalance((prev) => prev + refundAmount);
      toast.success(`₹${refundAmount.toFixed(2)} refunded to wallet!`);
      setShowConfirm(false);
    } catch (error) {
      toast.error(error.message || "Refund processing failed");
    } finally {
      setIsProcessing(false);
    }
  };

  // Format minutes as "Xm Ys".
  const formatTime = (minutes) => {
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    return `${mins}m ${secs}s`;
  };

  return (
    <div className="relative">
      <button
        disabled={order.isUrgent || timeLeft <= 0 || isProcessing || isRefunded}
        onClick={() => setShowConfirm(true)}
        className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
          order.isUrgent || timeLeft <= 0 || isRefunded
            ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
            : "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700"
        } ${isProcessing ? "opacity-75" : ""}`}
      >
        {isProcessing ? (
          <FaSpinner className="animate-spin" />
        ) : (
          <FaUndo className="flex-shrink-0" />
        )}
        <span>
          {order.isUrgent
            ? "Urgent (No Refund)"
            : isRefunded
            ? "Refunded"
            : timeLeft > 0
            ? `Refund (${formatTime(timeLeft)})`
            : "Refund Expired"}
        </span>
      </button>

      <AnimatePresence>
        {showConfirm && !isRefunded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold dark:text-white">Confirm Refund</h3>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-300"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="space-y-4">
                <p className="dark:text-gray-200">
                  You will receive:
                  <span className="block text-2xl font-bold text-green-500">
                    ₹{order.totalAmount.toFixed(2)}
                  </span>
                </p>
                <div className="grid grid-cols-2 gap-3 mt-6">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    <FaTimes />
                    Cancel
                  </button>
                  <button
                    onClick={handleRefund}
                    disabled={isProcessing}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-75"
                  >
                    {isProcessing ? (
                      <FaSpinner className="animate-spin" />
                    ) : (
                      <>
                        <FaCheck />
                        Confirm
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RefundButton;
